import { appConfig } from '@fs/zion-config'
import { getBrightspotConfig, type Bandwidth } from '@fs/zion-rootstech'

export type ImageSizes = Record<string, Record<string, number | boolean>>

export type BrightspotConfig = {
  imageSizes: ImageSizes
  query: {
    defaults: {
      visibilityRequest: { status: string }
      pageRequest: { page: number; limit: number }
      creatorTypes: string[]
      imageSize: number
    }
    manualChunkSize: number
    fetchPolicy: { NETWORK_ONLY: 'network-only'; CACHE_FIRST: 'cache-first' }
  }
  apiPaths: { delivery: string; cae: string }
  api: {
    key: string
    uri: string
    conferenceCmsUri: string
    upload: { key: string; url: string }
  }
  objectTypes: Record<string, string>
}

// Neutral fallback bandwidth: used both as the module-init default (before any
// real signal is available) and as the BrightspotConfigProvider's fallback when
// useBandwidth() reports 'unknown' or an unrecognized Split treatment.
export const FALLBACK_BANDWIDTH: Bandwidth = 'good'

// Unset in an environment leaves rootstech_brightspot5 with no effect there
export const getBrightspot5ApiUri = (): string | undefined => appConfig?.CLIENT_BRIGHTSPOT5_API_URL || undefined

export const getBrightspot5ConferenceUri = (): string | undefined => {
  const apiUri = getBrightspot5ApiUri()
  return apiUri && `${apiUri}/graphql/delivery/conference`
}

export const getBrightspot5UploadUri = (): string | undefined => {
  const apiUri = getBrightspot5ApiUri()
  return apiUri && `${apiUri}/rootstech/api/service/rootstech/file-upload-api/`
}

const getDefaultWidth = (): number => (typeof window !== 'undefined' && window.innerWidth) || 1920
const getDefaultDpr = (): number => (typeof window !== 'undefined' && window.devicePixelRatio) || 1

// Web wrapper around the shared config builder: injects the web app's env config.
// bandwidth/width/dpr default to a first-paint snapshot; BrightspotConfigProvider
// recomputes this with live values once useBandwidth() resolves.
export const getBrightspotWebConfig = (
  bandwidth: Bandwidth = FALLBACK_BANDWIDTH,
  width: number = getDefaultWidth(),
  dpr: number = getDefaultDpr()
): BrightspotConfig => getBrightspotConfig({ bandwidth, width, dpr, appConfig })

const brightspotConfig = getBrightspotWebConfig()

export default brightspotConfig

// Mutable live cell: lets non-component modules (GraphQL query builders that
// can't call hooks) read the latest bandwidth/viewport-aware imageSizes without
// becoming hooks themselves. BrightspotConfigProvider updates this on every
// resolved bandwidth/viewport change; until it mounts, callers get the
// module-init snapshot above.
let liveImageSizes: ImageSizes = brightspotConfig.imageSizes

export const setLiveImageSizes = (imageSizes: ImageSizes): void => {
  liveImageSizes = imageSizes
}

export const getLiveImageSizes = (): ImageSizes => liveImageSizes
